'use client';

/**
 * ExpensePolicyList — Displays a list of expense policy rules with
 * create / edit / delete / toggle actions. Opens ExpensePolicyRuleBuilder
 * as a modal for creating and editing rules.
 *
 * Used in:
 *   - HubSettings Approvals tab
 *   - DocumentModal (admin shortcut from DIN records)
 */

import React, { useState, useCallback } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Shield,
  Loader2,
  Building2,
  Tag,
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Repeat,
  FileWarning,
  CreditCard,
  Calendar,
  User,
  Copy,
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import { useExpensePolicies } from '@/hooks/useExpensePolicies';
import ExpensePolicyRuleBuilder from './ExpensePolicyRuleBuilder';
import type { ExpensePolicyRule, ExpensePolicyRuleType, ExpensePolicyActionType } from '@/lib/finance/expense/policyRules';
import { confirm } from '@/lib/notifications';

// ── Icon mapping for rule types ──────────────────────────────────────────────

const RULE_TYPE_ICONS: Partial<Record<ExpensePolicyRuleType, React.ReactNode>> = {
  spending_limit: <DollarSign className="w-4 h-4" />,
  category_restriction: <Tag className="w-4 h-4" />,
  approval_threshold: <Shield className="w-4 h-4" />,
  vendor_policy: <User className="w-4 h-4" />,
  payment_method_policy: <CreditCard className="w-4 h-4" />,
  daily_limit: <Calendar className="w-4 h-4" />,
  weekly_limit: <Calendar className="w-4 h-4" />,
  monthly_limit: <Calendar className="w-4 h-4" />,
  receipt_amount_threshold: <DollarSign className="w-4 h-4" />,
  duplicate_detection: <Copy className="w-4 h-4" />,
  recurring_expense: <Repeat className="w-4 h-4" />,
  missing_receipt: <FileWarning className="w-4 h-4" />,
  supplier_match_policy: <Building2 className="w-4 h-4" />,
  per_diem_policy: <Calendar className="w-4 h-4" />,
};

const ACTION_LABELS: Partial<Record<ExpensePolicyActionType, string>> = {
  block_submission: 'Block',
  require_approval: 'Require Approval',
  flag_for_review: 'Flag for Review',
  auto_categorize: 'Auto-Categorize',
  warn_user: 'Warn User',
  require_vendor_registration: 'Require Vendor Registration',
  auto_match_supplier: 'Auto-Match Supplier',
  auto_approve: 'Auto-Approve',
  auto_assign_supplier: 'Auto-Assign Supplier',
};

const RULE_TYPE_LABELS: Partial<Record<ExpensePolicyRuleType, string>> = {
  spending_limit: 'Spending Limit',
  category_restriction: 'Category Restriction',
  approval_threshold: 'Approval Threshold',
  vendor_policy: 'Vendor Policy',
  payment_method_policy: 'Payment Method',
  daily_limit: 'Daily Limit',
  weekly_limit: 'Weekly Limit',
  monthly_limit: 'Monthly Limit',
  receipt_amount_threshold: 'Receipt Amount Threshold',
  duplicate_detection: 'Duplicate Detection',
  recurring_expense: 'Recurring Expense',
  missing_receipt: 'Missing Receipt',
  supplier_match_policy: 'Supplier Match Policy',
  per_diem_policy: 'Per Diem Policy',
};

// ── Props ────────────────────────────────────────────────────────────────────

interface ExpensePolicyListProps {
  /** When true, renders a compact card suitable for embedding in a settings tab. */
  compact?: boolean;
  /** Optional title override. */
  title?: string;
  /** Whether the current user can manage policies. */
  canManage: boolean;
  /** Client ID (falls back to auth user's client_id). */
  clientId?: number;
}

// ── Component ────────────────────────────────────────────────────────────────

export default function ExpensePolicyList({
  compact = false,
  title = 'Expense Allocation & Policy Rules',
  canManage,
  clientId,
}: ExpensePolicyListProps) {
  const { currentTheme, actionColor } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  const { policies, loading, error, refetch } = useExpensePolicies(true);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<ExpensePolicyRule | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const resolvedClientId = clientId ?? 0;

  const handleOpenCreate = useCallback(() => {
    setEditingPolicy(null);
    setBuilderOpen(true);
  }, []);

  const handleOpenEdit = useCallback((policy: ExpensePolicyRule) => {
    setEditingPolicy(policy);
    setBuilderOpen(true);
  }, []);

  const handleSave = useCallback(async (policy: ExpensePolicyRule) => {
    const token = authService.getTokens()?.access_token;
    if (!token) return;

    const isEditing = policies.some((p) => p.id === policy.id);
    const url = isEditing
      ? `/api/v1/finance/expense-policies/${policy.id}`
      : '/api/v1/finance/expense-policies';
    const method = isEditing ? 'PUT' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(policy),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to save policy (${res.status})`);
      }
      await refetch();
      setBuilderOpen(false);
      setEditingPolicy(null);
    } catch (err) {
      console.error('[ExpensePolicyList] Save error:', err);
      throw err;
    }
  }, [policies, refetch]);

  const handleToggle = useCallback(async (policy: ExpensePolicyRule) => {
    const token = authService.getTokens()?.access_token;
    if (!token) return;
    setTogglingId(policy.id);
    try {
      const res = await fetch(`/api/v1/finance/expense-policies/${policy.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ is_active: !policy.is_active }),
      });
      if (!res.ok) throw new Error(`Failed to toggle policy (${res.status})`);
      await refetch();
    } catch (err) {
      console.error('[ExpensePolicyList] Toggle error:', err);
    } finally {
      setTogglingId(null);
    }
  }, [refetch]);

  const handleDelete = useCallback(async (policy: ExpensePolicyRule) => {
    if (!(await confirm({ message: `Delete policy "${policy.rule_name}"? This cannot be undone.`, variant: 'danger' }))) return;
    const token = authService.getTokens()?.access_token;
    if (!token) return;
    setDeletingId(policy.id);
    try {
      const res = await fetch(`/api/v1/finance/expense-policies/${policy.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to delete policy (${res.status})`);
      await refetch();
    } catch (err) {
      console.error('[ExpensePolicyList] Delete error:', err);
    } finally {
      setDeletingId(null);
    }
  }, [refetch]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={`bg-white dark:bg-gray-950 rounded-xl border border-gray-200 dark:border-gray-800 ${compact ? 'p-4' : 'p-6'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg" style={{ backgroundColor: `${actionColor}15` }}>
            <Shield className="w-5 h-5" style={{ color: actionColor }} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Configure rules to auto-assign suppliers, restrict categories, enforce spending limits, and more.
            </p>
          </div>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={handleOpenCreate}
            className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 text-sm font-medium transition-opacity"
            style={{ backgroundColor: actionColor }}
          >
            <Plus className="w-4 h-4" />
            Add Rule
          </button>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-200">
          {error}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && policies.length === 0 && (
        <div className="text-center py-10 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700">
          <Shield className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No expense policy rules configured yet.
          </p>
          {canManage && (
            <p className="text-xs text-gray-400 mt-1">
              Click &ldquo;Add Rule&rdquo; to create your first rule, e.g. &ldquo;Fuel expenses → assign to Shell&rdquo;.
            </p>
          )}
        </div>
      )}

      {/* Policy list */}
      {!loading && !error && policies.length > 0 && (
        <div className="space-y-2">
          {policies.map((policy) => {
            const icon = RULE_TYPE_ICONS[policy.rule_type] || <Shield className="w-4 h-4" />;
            const typeLabel = RULE_TYPE_LABELS[policy.rule_type] || policy.rule_type;
            const actionLabel = ACTION_LABELS[policy.action_type] || policy.action_type;
            const isToggling = togglingId === policy.id;
            const isDeleting = deletingId === policy.id;

            return (
              <div
                key={policy.id}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                  policy.is_active
                    ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50'
                    : 'border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/20 opacity-60'
                }`}
              >
                {/* Icon */}
                <div
                  className={`p-2 rounded-lg ${policy.is_active ? '' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`}
                  style={policy.is_active ? { backgroundColor: `${actionColor}15`, color: actionColor } : undefined}
                >
                  {icon}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {policy.rule_name}
                    </span>
                    {policy.is_active ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                        <CheckCircle2 className="w-3 h-3" /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-400">
                        <XCircle className="w-3 h-3" /> Inactive
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    <span>{typeLabel}</span>
                    <span className="text-gray-300 dark:text-gray-600">→</span>
                    <span>{actionLabel}</span>
                    {policy.action_type === 'auto_assign_supplier' && policy.action_config.contact_name && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">→</span>
                        <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400">
                          <Building2 className="w-3 h-3" /> {policy.action_config.contact_name}
                        </span>
                      </>
                    )}
                    {policy.priority > 0 && (
                      <span className="text-gray-400">· P{policy.priority}</span>
                    )}
                  </div>
                  {/* Condition summary */}
                  <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                    {policy.conditions?.all && policy.conditions.all.length > 0 && (
                      <span>{policy.conditions.all.length} condition{policy.conditions.all.length !== 1 ? 's' : ''}</span>
                    )}
                    {policy.conditions?.any && policy.conditions.any.length > 0 && (
                      <span>{(policy.conditions?.all?.length || 0) > 0 ? ' + ' : ''}{policy.conditions.any.length} OR condition{policy.conditions.any.length !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                {canManage && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => void handleToggle(policy)}
                      disabled={isToggling}
                      title={policy.is_active ? 'Deactivate' : 'Activate'}
                      className="p-1.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      {isToggling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleOpenEdit(policy)}
                      title="Edit"
                      className="p-1.5 rounded text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDelete(policy)}
                      disabled={isDeleting}
                      title="Delete"
                      className="p-1.5 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Builder Modal */}
      {builderOpen && (
        <ExpensePolicyRuleBuilder
          isOpen={builderOpen}
          onClose={() => { setBuilderOpen(false); setEditingPolicy(null); }}
          onSave={handleSave}
          editingPolicy={editingPolicy}
          clientId={resolvedClientId}
        />
      )}
    </div>
  );
}
